import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { LenderProfileForm } from "./LenderProfileForm";
import { LenderProfileDetail } from "./LenderProfileDetail";
import { EnhancedLenderCard } from "./EnhancedLenderCard";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface LenderProfile {
  id: string;
  lender_name: string;
  short_name: string | null;
  agreed_interest_rate: number;
  loan_start_date: string;
  payment_schedule: string;
  is_active: boolean;
  notes: string | null;
  created_at: string;
}

export const LenderProfileList = () => {
  const [lenders, setLenders] = useState<LenderProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedLender, setSelectedLender] = useState<LenderProfile | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [lenderToDelete, setLenderToDelete] = useState<LenderProfile | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchLenders();
  }, []);

  const fetchLenders = async () => {
    try {
      const { data, error } = await supabase
        .from("lender_profiles")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setLenders(data || []);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedLender(null);
    setShowForm(true);
  };

  const handleEdit = (lender: LenderProfile) => {
    setSelectedLender(lender);
    setShowForm(true);
  };

  const handleViewDetail = (lender: LenderProfile) => {
    setSelectedLender(lender);
    setShowDetail(true);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    fetchLenders();
  };

  const handleArchive = async (e: React.MouseEvent, lender: LenderProfile) => {
    e.stopPropagation();
    try {
      const { error } = await supabase
        .from("lender_profiles")
        .update({ is_active: !lender.is_active })
        .eq("id", lender.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: `Lender ${lender.is_active ? "archived" : "activated"} successfully`,
      });
      fetchLenders();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleDeleteClick = (e: React.MouseEvent, lender: LenderProfile) => {
    e.stopPropagation();
    setLenderToDelete(lender);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!lenderToDelete) return;

    try {
      const { error } = await supabase
        .from("lender_profiles")
        .delete()
        .eq("id", lenderToDelete.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Lender deleted successfully",
      });
      fetchLenders();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setDeleteDialogOpen(false);
      setLenderToDelete(null);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading lenders...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">All Lender Profiles</h3>
        <Button onClick={handleCreate}>
          <Plus className="h-4 w-4 mr-2" />
          New Lender
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {lenders.map((lender) => (
          <EnhancedLenderCard
            key={lender.id}
            lender={lender}
            onEdit={() => handleEdit(lender)}
            onViewLoans={() => handleViewDetail(lender)}
            onArchive={() => handleArchive({} as React.MouseEvent, lender)}
            onDelete={() => {
              setLenderToDelete(lender);
              setDeleteDialogOpen(true);
            }}
            onStatusChange={(status) => {
              // Handle status change
              console.log("Status changed to:", status);
            }}
          />
        ))}
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {selectedLender ? "Edit Lender" : "New Lender Profile"}
            </DialogTitle>
          </DialogHeader>
          <LenderProfileForm
            lender={selectedLender}
            onSuccess={handleFormSuccess}
            onCancel={() => setShowForm(false)}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedLender?.lender_name}</DialogTitle>
          </DialogHeader>
          {selectedLender && (
            <LenderProfileDetail
              lender={selectedLender}
              onEdit={() => {
                setShowDetail(false);
                handleEdit(selectedLender);
              }}
              onClose={() => setShowDetail(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Lender</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{lenderToDelete?.lender_name}"? This action cannot be undone and will also delete all associated transactions.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
